import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, X, TrendingUp, TrendingDown, Eye } from 'lucide-react';

const Accounts = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [viewingAccount, setViewingAccount] = useState(null);
  const [accountLedger, setAccountLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [formData, setFormData] = useState({
    account_name: '',
    account_type: 'assets',
    parent_account_id: '',
    description: '',
    is_bank_account: false,
    initial_balance: 0
  });

  const accountTypes = [
    { value: 'assets', label: 'Assets', color: 'blue' },
    { value: 'liabilities', label: 'Liabilities', color: 'red' },
    { value: 'equity', label: 'Equity', color: 'purple' },
    { value: 'revenue', label: 'Revenue', color: 'green' },
    { value: 'expenses', label: 'Expenses', color: 'orange' }
  ];

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/accounts');
      setAccounts(response.data);
    } catch (error) {
      toast.error('Failed to fetch accounts');
    } finally {
      setLoading(false);
    }
  };

  // Open the account details viewer — pulls all journal lines that
  // touched this account so the user sees every transaction that
  // built up the balance.
  const openAccountDetails = async (account) => {
    setViewingAccount(account);
    setAccountLedger(null);
    setLedgerLoading(true);
    try {
      // Try the dedicated endpoint first; fall back to journals filter.
      let lines = [];
      try {
        const r = await axiosInstance.get(`/accounts/${account.id}/ledger?limit=500`);
        lines = r.data?.lines || r.data?.entries || r.data || [];
      } catch (_) {
        const r2 = await axiosInstance.get(`/journals?account_id=${account.id}&limit=500`);
        const journals = r2.data || [];
        lines = [];
        for (const j of journals) {
          for (const ln of (j.lines || [])) {
            if (ln.account_id === account.id) {
              lines.push({
                date:       j.date || j.entry_date,
                journal_no: j.journal_number || j.entry_number,
                ref:        j.reference || j.source_doc_no,
                description: ln.description || j.description,
                debit:      ln.debit || 0,
                credit:     ln.credit || 0,
              });
            }
          }
        }
      }
      setAccountLedger(lines);
    } catch (e) {
      toast.error('Failed to load account ledger');
      setAccountLedger([]);
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingAccount) {
        await axiosInstance.put(`/accounts/${editingAccount.id}`, formData);
        toast.success('Account updated successfully');
      } else {
        await axiosInstance.post('/accounts', formData);
        toast.success('Account created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this account?')) {
      try {
        await axiosInstance.delete(`/accounts/${id}`);
        toast.success('Account deleted successfully');
        fetchAccounts();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete account');
      }
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      account_name: account.account_name,
      account_type: account.account_type,
      parent_account_id: account.parent_account_id || '',
      description: account.description || '',
      is_bank_account: account.is_bank_account || false,
      initial_balance: 0
    });
    setShowModal(true);
  };

  const handleStatusChange = async (id, status) => {
    try {
      await axiosInstance.put(`/accounts/${id}/status?status=${status}`);
      toast.success('Status updated');
      fetchAccounts();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const resetForm = () => {
    setFormData({
      account_name: '',
      account_type: 'assets',
      parent_account_id: '',
      description: '',
      is_bank_account: false,
      initial_balance: 0
    });
    setEditingAccount(null);
  };

  const parentAccounts = accounts.filter(acc => !acc.parent_account_id);

  if (loading) {
    return <div className="text-center py-12">Loading accounts...</div>;
  }

  const accountsByType = accountTypes.reduce((acc, type) => {
    acc[type.value] = accounts.filter(a => a.account_type === type.value);
    return acc;
  }, {});

  return (
    <div data-testid="accounts-page">
      <div className="page-header">
        <h2 className="page-title">Chart of Accounts</h2>
        <button
          data-testid="add-account-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Account
        </button>
      </div>

      {accountTypes.map((type) => {
        const typeAccounts = accountsByType[type.value];
        if (typeAccounts.length === 0) return null;

        return (
          <div key={type.value} className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-4 h-4 rounded stat-icon ${type.color}`}></div>
              <h3 className="text-xl font-bold text-gray-900">{type.label}</h3>
              <span className="text-sm text-gray-500">({typeAccounts.length} accounts)</span>
            </div>

            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Account ID</th>
                    <th>Account Name</th>
                    <th>Type</th>
                    <th>Balance</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {typeAccounts.map((account) => (
                    <tr key={account.id} data-testid={`account-row-${account.id}`}
                         onClick={() => openAccountDetails(account)}
                         className="cursor-pointer hover:bg-indigo-50/40 transition-colors">
                      <td className="font-semibold">{account.account_number}</td>
                      <td>
                        <div>
                          <p className="font-semibold">{account.account_name}</p>
                          {account.description && (
                            <p className="text-sm text-gray-500">{account.description}</p>
                          )}
                          {account.is_bank_account && (
                            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded mt-1 inline-block">
                              Bank Account
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge ${type.value}`}>
                          {type.label}
                        </span>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <span className={`font-bold ${account.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {Math.abs(account.balance).toLocaleString()} SAR
                          </span>
                          {account.balance >= 0 ? (
                            <TrendingUp className="w-4 h-4 text-green-600" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          DR: {account.debit_balance.toLocaleString()} SAR | CR: {account.credit_balance.toLocaleString()} SAR
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <select
                          value={account.status}
                          onChange={(e) => handleStatusChange(account.id, e.target.value)}
                          className={`status-badge ${account.status} cursor-pointer`}
                          style={{ border: 'none', outline: 'none' }}
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="action-buttons">
                          <button
                            data-testid={`view-account-${account.id}`}
                            onClick={() => openAccountDetails(account)}
                            className="btn-icon"
                            title="View Details + Ledger"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`edit-account-${account.id}`}
                            onClick={() => handleEdit(account)}
                            className="btn-icon edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            data-testid={`delete-account-${account.id}`}
                            onClick={() => handleDelete(account.id)}
                            className="btn-icon delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingAccount ? 'Edit Account' : 'New Account'}
              </h3>
              <button
                data-testid="close-modal-button"
                onClick={() => setShowModal(false)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {editingAccount && (
                <div className="form-group">
                  <label className="form-label">Account ID</label>
                  <input
                    type="text"
                    value={editingAccount.account_number}
                    className="form-input bg-gray-100"
                    disabled
                    readOnly
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Account Name *</label>
                <input
                  data-testid="account-name-input"
                  type="text"
                  value={formData.account_name}
                  onChange={(e) => setFormData({ ...formData, account_name: e.target.value })}
                  className="form-input"
                  placeholder="Enter account name"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Account Type *</label>
                <select
                  data-testid="account-type-select"
                  value={formData.account_type}
                  onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                  className="form-select"
                  required
                >
                  {accountTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Parent Account (Optional)</label>
                <select
                  data-testid="parent-account-select"
                  value={formData.parent_account_id}
                  onChange={(e) => setFormData({ ...formData, parent_account_id: e.target.value })}
                  className="form-select"
                >
                  <option value="">None (Main Account)</option>
                  {parentAccounts
                    .filter(acc => acc.account_type === formData.account_type)
                    .map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.account_name} ({acc.account_number})
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  data-testid="account-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Account description"
                />
              </div>

              <div className="form-group">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    data-testid="is-bank-account-checkbox"
                    type="checkbox"
                    checked={formData.is_bank_account}
                    onChange={(e) => setFormData({ ...formData, is_bank_account: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="form-label mb-0">This is a Bank Account</span>
                </label>
              </div>

              {!editingAccount && (
                <div className="form-group">
                  <label className="form-label">Initial Balance</label>
                  <input
                    data-testid="initial-balance-input"
                    type="number"
                    value={formData.initial_balance}
                    onChange={(e) => setFormData({ ...formData, initial_balance: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    step="0.01"
                    placeholder="0.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Positive for debit balance, negative for credit balance
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-account-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingAccount ? 'Update Account' : 'Create Account'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Account Details + Ledger viewer modal */}
      {viewingAccount && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
              data-testid="account-detail-modal"
              onClick={() => setViewingAccount(null)}>
          <div onClick={(e) => e.stopPropagation()}
                className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-lg font-bold inline-flex items-center gap-2">
                  <span className="font-mono text-indigo-700">{viewingAccount.account_number}</span>
                  <span>{viewingAccount.account_name}</span>
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Type: <strong className="capitalize">{viewingAccount.account_type}</strong>
                  {viewingAccount.is_bank_account && (
                    <span className="ml-2 px-2 py-0.5 rounded bg-blue-100 text-blue-700">Bank</span>
                  )}
                </p>
              </div>
              <button onClick={() => setViewingAccount(null)}
                      className="text-gray-400 hover:text-gray-700 text-2xl leading-none">×</button>
            </div>

            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3 p-4 bg-gray-50 border-b border-gray-200">
              <div className="p-3 bg-white rounded border border-gray-200">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Net Balance</div>
                <div className={`text-2xl font-bold ${(viewingAccount.balance || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {Math.abs(viewingAccount.balance || 0).toLocaleString()} SAR
                </div>
              </div>
              <div className="p-3 bg-white rounded border border-gray-200">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Total Debit</div>
                <div className="text-xl font-bold text-emerald-700">
                  {(viewingAccount.debit_balance || 0).toLocaleString()} SAR
                </div>
              </div>
              <div className="p-3 bg-white rounded border border-gray-200">
                <div className="text-[10px] uppercase tracking-wider text-gray-500">Total Credit</div>
                <div className="text-xl font-bold text-rose-700">
                  {(viewingAccount.credit_balance || 0).toLocaleString()} SAR
                </div>
              </div>
            </div>

            {viewingAccount.description && (
              <div className="px-5 py-3 border-b border-gray-200 text-sm text-gray-700">
                <span className="text-xs uppercase tracking-wider text-gray-500 font-semibold mr-2">Description:</span>
                {viewingAccount.description}
              </div>
            )}

            {/* Ledger Transactions */}
            <div className="p-5">
              <h3 className="text-sm font-bold text-gray-800 mb-3 inline-flex items-center gap-2">
                📒 Account Ledger / Transactions
                {accountLedger && (
                  <span className="text-xs font-normal text-gray-500">
                    · {accountLedger.length} transaction(s)
                  </span>
                )}
              </h3>

              {ledgerLoading ? (
                <div className="text-center py-10 text-gray-500">جاري التحميل...</div>
              ) : !accountLedger || accountLedger.length === 0 ? (
                <div className="text-center py-10 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                  لا توجد حركات على هذا الحساب حتى الآن
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-gray-600 text-xs">
                      <tr>
                        <th className="text-left px-3 py-2">
                          Date
                          <div className="text-[9px] font-normal text-gray-400 normal-case">
                            (Bill/Expense date)
                          </div>
                        </th>
                        <th className="text-left px-3 py-2">
                          Posted
                          <div className="text-[9px] font-normal text-gray-400 normal-case">
                            (Journal date)
                          </div>
                        </th>
                        <th className="text-left px-3 py-2">Journal #</th>
                        <th className="text-left px-3 py-2">Reference</th>
                        <th className="text-left px-3 py-2">Party</th>
                        <th className="text-left px-3 py-2">Description</th>
                        <th className="text-right px-3 py-2">Debit</th>
                        <th className="text-right px-3 py-2">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountLedger.map((ln, i) => (
                        <tr key={i} className="border-t border-gray-100 hover:bg-gray-50"
                            data-testid={`ledger-row-${i}`}>
                          <td className="px-3 py-2 text-xs text-gray-800 font-mono whitespace-nowrap font-semibold">
                            {ln.date || '—'}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-400 font-mono whitespace-nowrap"
                              title="Journal posting date (accounting entry)">
                            {ln.posting_date && ln.posting_date !== ln.date
                              ? ln.posting_date : ''}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-indigo-700">{ln.journal_no || ln.entry_number || '—'}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">{ln.ref || ln.reference || '—'}</td>
                          <td className="px-3 py-2 text-xs text-gray-700">{ln.party || '—'}</td>
                          <td className="px-3 py-2 text-xs">{ln.description || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono text-emerald-700">
                            {Number(ln.debit || 0) > 0 ? Number(ln.debit).toLocaleString() : ''}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-rose-700">
                            {Number(ln.credit || 0) > 0 ? Number(ln.credit).toLocaleString() : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;